import * as React from 'react';
import {
  Typography,
  Paper,
  Box,
  Container,
  Pagination,
  Button,
  Grid,
  OutlinedInput,
  InputAdornment,
  Stack,
  Skeleton,
} from '@mui/material';
import { connect } from "react-redux";
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';
import { EventCard } from "../../app/components";
import { searchEvents } from '../../app/store/actions/eventActions';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { motion, useReducedMotion } from 'framer-motion';
import { useSearchParams, useLocation } from 'react-router-dom';
import { usesPlatformChrome } from '../../app/components/platform/pageChrome';
import {
  PlatformPage,
  PlatformPageHeader,
  PlatformCard,
  PlatformSearchBar,
  PlatformSearchRow,
  PlatformButton,
  PlatformEventCard,
  PlatformEmptyState,
  PlatformFilterChip,
} from '../../app/components/platform';
import { platformColors, platformFonts, platformSearchInputSx } from '../../app/components/tickitPlatformTheme';
import { useThemeMode } from '../../app/components/theme';
import {
  MarketingPage,
  MarketingCard,
  MarketingButton,
} from '../../app/components/marketing';
import {
  marketingFonts,
  marketingColors,
  marketingRadius,
  marketingSearchInputSx,
  sectionLabelSx,
  sectionTitleSx,
} from '../../app/components/tickitMarketingTheme';

const DEFAULT_LIMIT = 12;

function parseDateParam(val, fallback) {
  if (!val) return fallback;
  const d = moment(val, 'YYYY-MM-DD', true);
  return d.isValid() ? d.toDate() : fallback;
}

function useDebouncedValue(value, delay = 350) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const searchOutlinedInputSx = {
  ...marketingSearchInputSx,
  height: { xs: '44px', md: '48px' },
};

function EventCardSkeleton() {
  return (
    <Box sx={{ borderRadius: marketingRadius.lg, overflow: 'hidden', border: `1px solid ${marketingColors.border}` }}>
      <Skeleton variant="rectangular" height={200} />
      <Box sx={{ p: 2 }}>
        <Skeleton width="80%" height={28} />
        <Skeleton width="60%" height={20} sx={{ mt: 1 }} />
      </Box>
    </Box>
  );
}

export const SearchEvents = ({ events, searchEvents, auth }) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const reduceMotion = useReducedMotion();
  const role = auth?.token?.split(' ')[0];
  const platformLayout = usesPlatformChrome(location.pathname, role);
  const adminRole = role === 'Admin' || role === 'Super' || role === 'Partner';
  const { mode } = useThemeMode();
  const filterInputSx = platformLayout
    ? { ...platformSearchInputSx, height: { xs: '44px', md: '48px' } }
    : searchOutlinedInputSx;
  const datePickerThemeClass =
    platformLayout && mode === 'dark' ? 'react-datepicker-dark' : 'react-datepicker-light';

  const defaultMin = new Date();
  const defaultMax = moment().add(1, 'year').toDate();

  const readStateFromUrl = React.useCallback(() => ({
    search: searchParams.get('search') || '',
    page: Math.max(1, parseInt(searchParams.get('page') || '1', 10)),
    minDate: parseDateParam(searchParams.get('minDate'), defaultMin),
    maxDate: parseDateParam(searchParams.get('maxDate'), defaultMax),
    limit: DEFAULT_LIMIT,
  }), [searchParams]);

  const [formData, setFormData] = React.useState(readStateFromUrl);
  const debouncedSearch = useDebouncedValue(formData.search);

  const buildApiPayload = React.useCallback((data) => ({
    ...data,
    minDate: moment(data.minDate).format('YYYY-MM-DD'),
    maxDate: moment(data.maxDate).format('YYYY-MM-DD'),
  }), []);

  const syncUrl = React.useCallback((data) => {
    const params = new URLSearchParams();
    if (data.search) params.set('search', data.search);
    if (data.page > 1) params.set('page', String(data.page));
    const minStr = moment(data.minDate).format('YYYY-MM-DD');
    const maxStr = moment(data.maxDate).format('YYYY-MM-DD');
    if (minStr !== moment(defaultMin).format('YYYY-MM-DD')) params.set('minDate', minStr);
    if (maxStr !== moment(defaultMax).format('YYYY-MM-DD')) params.set('maxDate', maxStr);
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  React.useEffect(() => {
    setFormData(readStateFromUrl());
  }, [searchParams]);

  React.useEffect(() => {
    searchEvents(buildApiPayload(readStateFromUrl()));
  }, []);

  const skipDebouncedFetch = React.useRef(true);
  React.useEffect(() => {
    if (skipDebouncedFetch.current) {
      skipDebouncedFetch.current = false;
      return;
    }
    const next = { ...formData, search: debouncedSearch, page: 1 };
    setFormData(next);
    syncUrl(next);
    searchEvents(buildApiPayload(next));
  }, [debouncedSearch]);

  const { minDate, maxDate, search, page } = formData;
  const totalDocs = events?.results?.totalDocs ?? events?.results?.docs?.length ?? 0;
  const docs = events?.results?.docs || [];

  const applySearch = (overrides = {}) => {
    const next = { ...formData, ...overrides };
    setFormData(next);
    syncUrl(next);
    searchEvents(buildApiPayload(next));
  };

  const handleClearFilter = () => {
    const reset = {
      search: '',
      page: 1,
      minDate: defaultMin,
      maxDate: defaultMax,
      limit: DEFAULT_LIMIT,
    };
    setFormData(reset);
    setSearchParams({}, { replace: true });
    searchEvents(buildApiPayload(reset));
  };

  const handlePageChange = (_e, value) => {
    applySearch({ page: value });
  };

  const handleSearchChange = (e) => {
    setFormData({ ...formData, search: e.target.value });
  };

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    const next = {
      ...formData,
      minDate: start || formData.minDate,
      maxDate: end || formData.maxDate,
      page: 1,
    };
    if (start && end) {
      setFormData(next);
      syncUrl(next);
      searchEvents(buildApiPayload(next));
    } else {
      setFormData(next);
    }
  };

  const applyPreset = (preset) => {
    const now = moment();
    let min = now.clone().startOf('day').toDate();
    let max = defaultMax;
    if (preset === 'week') max = now.clone().add(7, 'days').toDate();
    if (preset === 'month') max = now.clone().add(1, 'month').toDate();
    applySearch({ minDate: min, maxDate: max, page: 1 });
  };

  const filterBlock = (
    <Stack spacing={2}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={5}>
          {platformLayout ? (
            <PlatformSearchBar
              value={search}
              onChange={handleSearchChange}
              placeholder="Search events by name or venue..."
            />
          ) : (
            <OutlinedInput
              name="event-search"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search for events, artists, or venues..."
              fullWidth
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: marketingColors.gray400 }} />
                </InputAdornment>
              }
              sx={searchOutlinedInputSx}
            />
          )}
        </Grid>
        <Grid item xs={12} md={5}>
          <DatePicker
            selectsRange
            startDate={minDate}
            endDate={maxDate}
            onChange={handleDateRangeChange}
            isClearable
            placeholderText="Select date range"
            className={datePickerThemeClass}
            wrapperClassName="date-range-wrapper"
            popperClassName="date-range-popper"
            customInput={
              <OutlinedInput
                fullWidth
                placeholder="Select date range"
                readOnly
                sx={{
                  ...filterInputSx,
                  '& .MuiInputBase-input': {
                    ...(filterInputSx['& .MuiInputBase-input'] || {}),
                    cursor: 'pointer',
                  },
                }}
              />
            }
          />
        </Grid>
        <Grid item xs={12} md={2}>
          {platformLayout ? (
            <PlatformButton variant="primary" onClick={() => applySearch({ page: 1 })} sx={{ width: '100%' }}>
              Search
            </PlatformButton>
          ) : (
            <MarketingButton onClick={() => applySearch({ page: 1 })} fullWidth sx={{ height: { xs: 48, md: 56 } }}>
              Search
            </MarketingButton>
          )}
        </Grid>
      </Grid>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <PlatformFilterChip label="This week" active={false} onClick={() => applyPreset('week')} />
          <PlatformFilterChip label="This month" active={false} onClick={() => applyPreset('month')} />
        </Stack>
        {platformLayout ? (
          <PlatformButton variant="ghost" size="small" onClick={handleClearFilter}>
            Clear filters
          </PlatformButton>
        ) : (
          <MarketingButton variant="outline" onClick={handleClearFilter}>
            Clear filters
          </MarketingButton>
        )}
      </Stack>
    </Stack>
  );

  const resultsHeader = (
    <Typography
      component="p"
      aria-live="polite"
      sx={{
        mb: 2,
        fontFamily: platformLayout ? platformFonts.body : marketingFonts.body,
        fontWeight: 600,
        fontSize: platformLayout ? '0.875rem' : '1.35rem',
        color: platformLayout ? platformColors.text2 : marketingColors.p700,
      }}
    >
      {events?.isLoading
        ? 'Searching…'
        : totalDocs > 0
          ? `${totalDocs} event${totalDocs === 1 ? '' : 's'} found`
          : 'No events found'}
    </Typography>
  );

  const resultsGrid = events?.isLoading ? (
    <Grid container spacing={2}>
      {Array.from({ length: 6 }).map((_, i) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
          <EventCardSkeleton />
        </Grid>
      ))}
    </Grid>
  ) : docs.length > 0 ? (
    <Grid container spacing={2}>
      {docs.map((event) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={event._id}>
          {platformLayout && adminRole ? (
            <PlatformEventCard event={event} role={role} />
          ) : (
            <EventCard event={event} token={auth?.token} />
          )}
        </Grid>
      ))}
    </Grid>
  ) : (
    <PlatformEmptyState
      title="No events found"
      description="Try adjusting your search or date range."
      actionLabel="Clear filters"
      onAction={handleClearFilter}
    />
  );

  const pagination = docs.length > 0 && (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <Pagination
        count={events?.results?.pages || 1}
        page={page}
        onChange={handlePageChange}
        sx={{
          '& .MuiPaginationItem-root': {
            fontFamily: platformLayout ? platformFonts.body : marketingFonts.body,
            ...(platformLayout && {
              '&.Mui-selected': {
                bgcolor: `${platformColors.brandPale} !important`,
                color: platformColors.brand,
              },
            }),
          },
        }}
      />
    </Box>
  );

  const motionProps = reduceMotion
    ? {}
    : { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.35 } };

  if (platformLayout) {
    return (
      <PlatformPage>
        <motion.div {...motionProps}>
          <PlatformPageHeader
            title="All events"
            subtitle="Search and browse events across the platform"
          />
          <PlatformCard title="Search & filter" sx={{ mb: 2.5 }}>
            {filterBlock}
          </PlatformCard>
          {resultsHeader}
          {resultsGrid}
          {pagination}
        </motion.div>
      </PlatformPage>
    );
  }

  return (
    <MarketingPage maxWidth="xl">
      <motion.div {...motionProps}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography sx={{ ...sectionLabelSx, textAlign: 'center' }}>Events</Typography>
          <Typography variant="h2" sx={{ ...sectionTitleSx, fontSize: { xs: '2rem', md: '2.65rem' }, mb: 2 }}>
            Discover events
          </Typography>
          <Typography sx={{ color: marketingColors.gray500, fontFamily: marketingFonts.body, maxWidth: 560, mx: 'auto' }}>
            Search public listings, pick a date range, and get tickets for shows powered by Tickit.
          </Typography>
        </Box>
        <MarketingCard title="Search & filter" sx={{ mb: 3 }}>
          {filterBlock}
        </MarketingCard>
        <MarketingCard>
          {resultsHeader}
          {resultsGrid}
          {pagination}
        </MarketingCard>
      </motion.div>
    </MarketingPage>
  );
};

const mapStateToProps = (state) => ({
  events: state?.events,
  auth: state?.auth,
});

const mapDispatchToProps = { searchEvents };

export default connect(mapStateToProps, mapDispatchToProps)(SearchEvents);
